import { useState, useEffect, useCallback } from 'react'
import { Activity, Play, ChevronDown, ChevronRight, HelpCircle, RefreshCw, CheckCircle2, XCircle, AlertTriangle, Clock } from 'lucide-react'
import strategicEvaluator from '../../core/engine/strategicEvaluator'
import actualizarPorIndice from '../../core/updates/actualizarRemuneraciones'
import composeDictamenContext from '../../core/dictamenComposer'
import { buildRows, buildIndices } from '../../utils/rowsFactory'
import { compareResults } from '../../utils/dashboardComparison'
import { CASOS_TESTIGO } from '../../__tests__/fixtures/casosTestigo'

function formatTimeAgo(timestamp) {
  if (!timestamp) return null
  const diff = Date.now() - timestamp
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Ahora'
  if (mins < 60) return `Hace ${mins} min`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `Hace ${hrs} h`
  const days = Math.floor(hrs / 24)
  return days === 1 ? 'Hace 1 día' : `Hace ${days} días`
}

function runPipeline(caseDef) {
  const { buildConfig, expediente } = caseDef
  const rows = buildRows(
    buildConfig.months,
    buildConfig.startYear,
    buildConfig.startMonth,
    buildConfig.tipoActividad || undefined
  )

  let processedRows = rows
  if (buildConfig.simultaneous) {
    processedRows = rows.map((r, idx) => ({
      ...r,
      cuit: idx % 3 === 0 ? '20-12345678-9' : '20-87654321-0',
    }))
  }

  const indices = buildIndices()
  const rules = strategicEvaluator(expediente, processedRows)
  const actualizadas = actualizarPorIndice(
    processedRows,
    expediente.fallo,
    expediente.fechaCese,
    indices,
    'PASIVA_14290',
    rules,
    expediente
  )
  const ctx = composeDictamenContext(actualizadas, expediente, { mode: 'avanzado', activeRules: rules })
  return compareResults(ctx, caseDef.golden)
}

export default function DashboardIntegridad() {
  const [results, setResults] = useState(null)
  const [running, setRunning] = useState(false)
  const [lastRun, setLastRun] = useState(null)
  const [expanded, setExpanded] = useState({})
  const [showHelp, setShowHelp] = useState(false)

  const handleRun = useCallback(() => {
    setRunning(true)
    setExpanded({})

    // Use setTimeout to let React render the loading state before blocking
    setTimeout(() => {
      const out = []
      for (const caseDef of CASOS_TESTIGO) {
        try {
          const cr = runPipeline(caseDef)
          out.push({ id: caseDef.id, label: caseDef.label, ...cr })
        } catch (e) {
          out.push({
            id: caseDef.id,
            label: caseDef.label,
            passed: false,
            total: 0,
            passedCount: 0,
            error: e.message,
            fields: [],
          })
        }
      }
      setResults(out)
      setLastRun(Date.now())
      setRunning(false)
    }, 50)
  }, [])

  const totalPassed = results ? results.filter((r) => r.passed).length : 0
  const totalCases = results ? results.length : 0
  const totalFieldsPassed = results ? results.reduce((s, r) => s + r.passedCount, 0) : 0
  const totalFields = results ? results.reduce((s, r) => s + r.total, 0) : 0
  const pct = totalFields > 0 ? ((totalFieldsPassed / totalFields) * 100).toFixed(1) : 0

  const scoreColor = pct >= 90 ? 'emerald' : pct >= 80 ? 'amber' : 'rose'
  const isStale = lastRun && (Date.now() - lastRun > 86400000)

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-white">Dashboard de Integridad</h3>
          <p className="mt-0.5 text-xs text-[#9e9e9e]">
            Verifica que el motor de cálculo produce los resultados esperados comparando contra casos testigo.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastRun && (
            <span className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[11px] font-medium ${
              isStale
                ? 'border-amber-300/20 bg-amber-500/10 text-amber-200'
                : 'border-emerald-300/20 bg-emerald-500/10 text-emerald-300'
            }`}>
              <Clock className="h-3 w-3" />
              {formatTimeAgo(lastRun)}
            </span>
          )}
          <button
            type="button"
            onClick={handleRun}
            disabled={running}
            className="inline-flex items-center gap-2 rounded-xl border border-cyan-300/35 bg-cyan-500/12 px-4 py-2 text-xs font-semibold text-cyan-100 hover:bg-cyan-500/20 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {running ? (
              <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Ejecutando...</>
            ) : (
              <><Play className="h-3.5 w-3.5" /> Ejecutar Simulación</>
            )}
          </button>
          <button
            type="button"
            onClick={() => setShowHelp(!showHelp)}
            className="inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-[#24262d] px-3 py-2 text-xs text-[#9e9e9e] hover:text-white"
          >
            <HelpCircle className="h-3.5 w-3.5" /> Ayuda
          </button>
        </div>
      </div>

      {/* Score bar */}
      {results && (
        <div className={`rounded-xl border-2 p-4 ${
          scoreColor === 'emerald' ? 'border-emerald-300/25 bg-emerald-500/8' :
          scoreColor === 'amber' ? 'border-amber-300/20 bg-amber-500/8' :
          'border-rose-300/25 bg-rose-500/8'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {pct >= 100 ? (
                <CheckCircle2 className="h-8 w-8 text-emerald-300" />
              ) : pct >= 80 ? (
                <AlertTriangle className="h-8 w-8 text-amber-300" />
              ) : (
                <XCircle className="h-8 w-8 text-rose-300" />
              )}
              <div>
                <p className="text-lg font-bold text-white">{totalPassed}/{totalCases} casos OK</p>
                <p className="text-xs text-[#9e9e9e]">{totalFieldsPassed}/{totalFields} campos — {pct}% integridad</p>
              </div>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-bold ${
              scoreColor === 'emerald' ? 'bg-emerald-500/15 text-emerald-200' :
              scoreColor === 'amber' ? 'bg-amber-500/15 text-amber-200' :
              'bg-rose-500/15 text-rose-200'
            }`}>
              {pct >= 90 ? '✅ SALUDABLE' : pct >= 80 ? '⚠️ OBSERVADO' : '❌ CRÍTICO'}
            </span>
          </div>
        </div>
      )}

      {/* Stale warning */}
      {isStale && (
        <div className="rounded-xl border border-amber-300/20 bg-amber-500/8 px-4 py-3 text-xs text-amber-200">
          <AlertTriangle className="mr-1.5 inline h-3.5 w-3.5" />
          La última simulación fue hace más de 24 horas. Ejecutá una nueva simulación para verificar el estado actual del motor.
        </div>
      )}

      {/* Case cards */}
      {results && (
        <div className="space-y-2">
          {results.map((result) => {
            const isExpanded = expanded[result.id]
            const hasError = !!result.error
            const allPassed = result.passed && !hasError
            return (
              <div
                key={result.id}
                className={`rounded-xl border-2 ${
                  hasError ? 'border-rose-300/25' :
                  allPassed ? 'border-emerald-300/25' :
                  'border-rose-300/20'
                } bg-[#1f2128]`}
              >
                <button
                  type="button"
                  onClick={() => toggleExpand(result.id)}
                  className="flex w-full items-center justify-between px-4 py-3 text-left"
                >
                  <div className="flex items-center gap-3">
                    {hasError ? (
                      <XCircle className="h-4 w-4 shrink-0 text-rose-300" />
                    ) : allPassed ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-300" />
                    ) : (
                      <XCircle className="h-4 w-4 shrink-0 text-rose-300" />
                    )}
                    <span className="text-sm font-medium text-white">{result.label}</span>
                    <span className="rounded bg-[#24262d] px-2 py-0.5 text-[11px] text-[#9e9e9e]">
                      {result.passedCount}/{result.total}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {hasError && (
                      <span className="rounded bg-rose-500/10 px-2 py-0.5 text-[11px] text-rose-200">Error</span>
                    )}
                    {isExpanded ? <ChevronDown className="h-4 w-4 text-[#9e9e9e]" /> : <ChevronRight className="h-4 w-4 text-[#9e9e9e]" />}
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-white/10 px-4 pb-3 pt-2">
                    {hasError ? (
                      <p className="text-xs text-rose-300">Error: {result.error}</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="text-[#9e9e9e]">
                              <th className="px-2 py-1 font-medium">Campo</th>
                              <th className="px-2 py-1 font-medium">Esperado</th>
                              <th className="px-2 py-1 font-medium">Actual</th>
                              <th className="px-2 py-1 font-medium">Estado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.fields.map((f) => (
                              <tr key={f.field} className="border-t border-white/5">
                                <td className="px-2 py-1.5 font-medium text-white">{f.field}</td>
                                <td className="px-2 py-1.5 text-[#9e9e9e] font-mono">{f.expected != null ? f.expected : '-'}</td>
                                <td className="px-2 py-1.5 text-[#9e9e9e] font-mono">{f.actual != null ? f.actual : '-'}</td>
                                <td className="px-2 py-1.5">
                                  {f.passed ? (
                                    <span className="text-emerald-300">✅ OK</span>
                                  ) : (
                                    <span className="text-rose-300">❌</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Empty state */}
      {!results && !running && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-white/10 bg-[#1f2128] px-4 py-12">
          <Activity className="mb-3 h-8 w-8 text-[#9e9e9e]" />
          <p className="text-sm text-[#9e9e9e]">No hay resultados de integridad</p>
          <p className="mt-1 text-xs text-[#6e6e6e]">Ejecutá una simulación para verificar el estado del motor de cálculo.</p>
        </div>
      )}

      {/* Running overlay */}
      {running && (
        <div className="flex items-center justify-center rounded-xl border border-white/10 bg-[#1f2128] px-4 py-8">
          <RefreshCw className="mr-2 h-5 w-5 animate-spin text-cyan-300" />
          <span className="text-sm text-[#9e9e9e]">Ejecutando {CASOS_TESTIGO.length} casos testigo...</span>
        </div>
      )}

      {/* Help section */}
      {showHelp && (
        <div className="rounded-xl border border-cyan-300/15 bg-cyan-500/5 p-4">
          <h4 className="mb-2 text-sm font-semibold text-cyan-200">¿Qué es este dashboard?</h4>
          <div className="space-y-3 text-xs text-[#9e9e9e]">
            <p>
              <strong className="text-white">Casos Testigo:</strong> Son perfiles sintéticos que representan
              escenarios reales de cálculo (dependencia, mixto, docente, pensión, etc.). Cada caso tiene
              valores "golden" capturados de una ejecución correcta del motor.
            </p>
            <p>
              <strong className="text-white">Campos evaluados (10):</strong> haberBolsilloReajustado,
              retroactivoTotal, PBU, PC, PAP, movilidadAcumulada, divisorPromedioAplicado,
              scenarioKey, formulaPath e incrementoMensualPct. Los campos numéricos se comparan
              con precisión toFixed(4).
            </p>
            <p>
              <strong className="text-white">Interpretación:</strong> Un caso en rojo significa que el motor
              produce valores distintos a los esperados. Si todos los casos pasan (100%),
              el motor está funcionando correctamente para los escenarios cubiertos.
            </p>
            <p>
              <strong className="text-white">Casos incluidos ({CASOS_TESTIGO.length}):</strong>{' '}
              {CASOS_TESTIGO.map((c) => c.label).join(', ')}.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
